import { Badge } from '@/components/ui/badge';
import {
  FileSearch,
  BadgeCheck,
  ShieldCheck,
  AlertTriangle,
  Scale,
  Wand2,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="features-section"
      className="section-spacing scroll-mt-24"
    >
      <div className="section-container">
        <div className="text-center max-w-2xl mx-auto mb-14 sm:mb-20">
          <p data-animate className="kicker mb-4">Capabilities</p>
          <h2
            data-animate
            style={{ transitionDelay: '70ms' }}
            className="font-display text-2xl sm:text-3xl lg:text-[2.5rem] tracking-[-0.01em] leading-[1] lg:leading-[1.1] mb-4"
          >
            Six capabilities.
            One platform for<br /> the entire tender process.
          </h2>
          <p
            data-animate
            style={{ transitionDelay: '140ms' }}
            className="text-sm sm:text-base text-muted-foreground leading-relaxed"
          >
            Each designed for a different stage of the tender lifecycle.
            Together, they help teams manage the full tender process more efficiently.
          </p>
        </div>

        {/* Bento Grid - 2 rows of varied layouts */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-5">
          {/* Row 1: Document Review (large) + Eligibility (small) */}
          <div
            data-testid="feature-bento-item-document-review"
            data-animate
            className="md:col-span-7 rounded-2xl border bg-card p-6 sm:p-8 shadow-card bento-card bento-teal overflow-hidden"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <FileSearch className="w-5 h-5 text-primary" />
              <h3 className="text-base font-semibold text-foreground">Document Review</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5 max-w-lg">
              Every page is analyzed and key clauses are extracted automatically.<br className="hidden lg:block" /> The platform identifies sections and evaluates document completeness.
            </p>
            <div className="rounded-xl bg-white/80 border border-border/50 p-4 shadow-card">
              <div className="space-y-2.5">
                {[
                  { section: 'Scope of Work', status: 'Complete', pct: 100, color: 'text-primary' },
                  { section: 'Key Personnel Requirements', status: 'Complete', pct: 100, color: 'text-primary' },
                  { section: 'Insurance & Liability', status: 'Review needed', pct: 65, color: 'text-accent' },
                  { section: 'Pricing Schedule', status: 'Pending', pct: 30, color: 'text-muted-foreground/60' },
                ].map((item) => (
                  <div key={item.section} className="flex items-center gap-3">
                    <span className="text-xs text-foreground/70 w-40 truncate">{item.section}</span>
                    <div className="flex-1 score-bar">
                      <div className="score-bar-fill" style={{ width: `${item.pct}%` }} />
                    </div>
                    <span className={`font-mono text-[10px] ${item.color} w-20 text-right`}>{item.status}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Second screenshot: Extracted Key Clauses */}
            <div className="rounded-xl bg-white/80 border border-border/50 p-4 shadow-card mt-3">
              <p className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider mb-3">Extracted Key Clauses</p>
              <div className="space-y-2">
                {[
                  { clause: 'Liability cap limited to contract value', tag: 'Liability', tagColor: 'bg-red-50 text-red-600 border-red-100' },
                  { clause: 'Response deadline: 14 business days', tag: 'Timeline', tagColor: 'bg-amber-50 text-amber-600 border-amber-100' },
                  { clause: 'Minimum 3 years relevant experience required', tag: 'Eligibility', tagColor: 'bg-primary/8 text-primary border-primary/15' },
                ].map((item) => (
                  <div key={item.clause} className="flex items-start gap-2.5 rounded-lg bg-background/60 px-3 py-2 border border-border/30">
                    <span className={`shrink-0 text-[9px] font-medium px-1.5 py-0.5 rounded border ${item.tagColor}`}>{item.tag}</span>
                    <span className="text-xs text-foreground/70 leading-relaxed">{item.clause}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div
            data-testid="feature-bento-item-eligibility"
            data-animate
            style={{ transitionDelay: '80ms' }}
            className="md:col-span-5 rounded-2xl border bg-card p-6 sm:p-8 shadow-card bento-card bento-green overflow-hidden"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <BadgeCheck className="w-5 h-5 text-primary" />
              <h3 className="text-base font-semibold text-foreground">Eligibility Check</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Quickly understand whether your organization meets the key<br className="hidden lg:block" /> eligibility criteria.
            </p>
            <div className="space-y-2.5">
              {[
                { label: 'ABN Registration', pass: true, detail: 'Active since 2012' },
                { label: 'Minimum Turnover ($2M)', pass: true, detail: '$4.2M FY24' },
                { label: 'ISO 27001 Certification', pass: false, detail: 'Expired Mar 2024' },
                { label: 'Prior Govt. Experience', pass: true, detail: '12 contracts' },
                { label: 'Security Clearance (NV1)', pass: true, detail: '4 staff cleared' },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2.5 rounded-lg bg-white/80 px-3 py-2 border border-border/40">
                  {item.pass ? (
                    <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-destructive shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-foreground/80 block">{item.label}</span>
                    <span className="text-[10px] text-muted-foreground">{item.detail}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Badge variant="outline" className="text-[10px] border-primary/30 text-primary">4 of 5 passed</Badge>
              <Badge variant="outline" className="text-[10px] border-destructive/30 text-destructive">1 gap</Badge>
            </div>
          </div>

          {/* Row 2: Compliance (medium) + Risk Flags (medium-dark) */}
          <div
            data-testid="feature-bento-item-compliance"
            data-animate
            style={{ transitionDelay: '160ms' }}
            className="md:col-span-5 rounded-2xl border bg-card p-6 sm:p-8 shadow-card bento-card bento-orange overflow-hidden"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <h3 className="text-base font-semibold text-foreground">Compliance Matrix</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              A compliance matrix that builds itself. Every submission<br className="hidden lg:block" /> requirement mapped.
            </p>
            <div className="rounded-xl bg-white/80 border border-border/50 overflow-hidden">
              {[
                { req: 'Company profile (3pg)', src: 'S6.1', status: 'Ready' },
                { req: 'Financial statements', src: 'S6.2', status: 'Missing' },
                { req: 'Key personnel CVs', src: 'S6.3', status: 'Ready' },
                { req: 'Methodology statement', src: 'S6.4', status: 'Draft' },
              ].map((row, i) => (
                <div key={row.req} className={`flex items-center gap-2 px-3 py-2.5 text-xs ${
                  i < 3 ? 'border-b border-border/30' : ''
                }`}>
                  <span className="font-mono text-muted-foreground/50 w-8 shrink-0">{row.src}</span>
                  <span className="flex-1 text-foreground/80 truncate">{row.req}</span>
                  <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${
                    row.status === 'Ready' ? 'bg-primary/8 text-primary'
                      : row.status === 'Missing' ? 'bg-destructive/8 text-destructive'
                      : 'bg-accent/8 text-accent'
                  }`}>{row.status}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            data-testid="feature-bento-item-risk-flags"
            data-animate
            style={{ transitionDelay: '240ms' }}
            className="md:col-span-7 rounded-2xl border p-6 sm:p-8 shadow-card bento-card bento-slate overflow-hidden"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h3 className="text-base font-semibold">Risk Flags & Gaps</h3>
            </div>
            <p className="text-sm text-white/50 leading-relaxed mb-5 max-w-md">
              Risks surfaced with context. From tight timelines to unusual liability clauses.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-xl bg-red-500/10 border border-red-500/15 p-4">
                <p className="text-[10px] font-medium text-red-400 uppercase tracking-wider mb-1">High Risk</p>
                <p className="text-xs text-white/60 leading-relaxed">Liquidated damages at 5-day threshold. Industry standard is 10–15 days.</p>
              </div>
              <div className="rounded-xl bg-amber-500/10 border border-amber-500/15 p-4">
                <p className="text-[10px] font-medium text-amber-400 uppercase tracking-wider mb-1">Medium Risk</p>
                <p className="text-xs text-white/60 leading-relaxed">16-week timeline is tight. Past projects averaged 20 weeks.</p>
              </div>
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/15 p-4">
                <p className="text-[10px] font-medium text-emerald-400 uppercase tracking-wider mb-1">Opportunity</p>
                <p className="text-xs text-white/60 leading-relaxed">Value-for-money at 40% favours your competitive pricing.</p>
              </div>
            </div>
          </div>

          {/* Row 3: Bid/No-Bid (medium) + Draft Generation (large) */}
          <div
            data-testid="feature-bento-item-bid-no-bid"
            data-animate
            style={{ transitionDelay: '320ms' }}
            className="md:col-span-4 rounded-2xl border bg-card p-6 sm:p-8 shadow-card bento-card bento-purple overflow-hidden"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <Scale className="w-5 h-5 text-primary" />
              <h3 className="text-base font-semibold text-foreground">Bid / No-Bid</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              Make bid decisions based on structured analysis.
            </p>
            <div className="space-y-4 mb-5">
              {[
                { label: 'Strategic fit', value: 82 },
                { label: 'Capability match', value: 68 },
                { label: 'Risk exposure', value: 45 },
              ].map((f) => (
                <div key={f.label}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-muted-foreground">{f.label}</span>
                    <span className="font-mono text-foreground/80">{f.value}%</span>
                  </div>
                  <div className="score-bar">
                    <div className="score-bar-fill" style={{ width: `${f.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-baseline gap-2 pt-4 border-t border-border/40">
              <span className="font-display text-2xl text-primary">67</span>
              <span className="text-xs text-muted-foreground">/ 100 — Bid with conditions</span>
            </div>
          </div>

          <div
            data-testid="feature-bento-item-draft-generation"
            data-animate
            style={{ transitionDelay: '400ms' }}
            className="md:col-span-8 rounded-2xl border bg-card p-6 sm:p-8 shadow-card bento-card bento-rose overflow-hidden"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <Wand2 className="w-5 h-5 text-primary" />
              <h3 className="text-base font-semibold text-foreground">Draft Generation</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-5">
              From raw notes to structured, evaluation-ready responses. Saves 60% of drafting time.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white/70 border border-border/40 p-4">
                <p className="text-[10px] font-medium text-muted-foreground/60 uppercase tracking-wider mb-3">Raw Notes</p>
                <div className="mono-snippet space-y-1.5">
                  <p>- similar project dept health 2022</p>
                  <p>- sarah PM, mike tech lead</p>
                  <p>- agile, 12 sprints, under budget</p>
                  <p>- client extended contract</p>
                </div>
              </div>
              <div className="rounded-xl bg-primary/[0.04] border border-primary/15 p-4">
                <p className="text-[10px] font-medium text-primary uppercase tracking-wider mb-3">Structured Draft</p>
                <div className="text-xs text-foreground/80 space-y-2 leading-relaxed">
                  <p><span className="text-primary font-medium">Experience:</span> Comparable digital transformation project for Dept of Health, 2022...</p>
                  <p><span className="text-primary font-medium">Team:</span> Sarah Chen (PM, 8 yrs), Michael Torres (Tech Lead, 12 yrs)...</p>
                  <p><span className="text-primary font-medium">Risk:</span> Data migration addressed via proven custom ETL framework...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
